/*  ========================================================================
    # Section - Hero Slider
    ========================================================================  */

import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import HeroSliderNavigation from '../../components/HeroSliderNavigation';
import Lightbox from '../../components/Lightbox';
import TrailerPreview from '../../components/TrailerPreview';
import useHeroSliderPagination from '../../hooks/useHeroSliderPagination';
import useTabVisible from '../../hooks/useTabVisible';
import { useSwipeable } from 'react-swipeable';
import useBreakpoints from '../../hooks/useBreakpoints';
import { useInView } from 'react-intersection-observer';

const extendSlidesIfRequired = slides => {
  // If there is only one slide, the slider is static. No changes required.
  if (slides.length === 1) return slides;

  // We need at least 5 elements for all animations to work properly
  // Append the slides as often as required to achieve this minimum length of 5.
  const extendedSlides = [...slides];
  while (extendedSlides.length < 5) {
    extendedSlides.push(...slides);
  }

  // Return the extended, final set of slides
  return extendedSlides;
};

const HeroSlider = ({ definedSlides }) => {
  const { ref, inView } = useInView({
    rootMargin: '300px',
    threshold: 0,
    initialInView: true
  });

  // Ref to the main component div to notify when the app has loaded
  const appRef = useRef(null);
  useEffect(() => {
    if (appRef.current) {
      appRef.current.parentNode.setAttribute('data-react-loaded', true);
    }
  }, [appRef.current]);

  // Prepare the slides
  const slides = extendSlidesIfRequired(definedSlides);
  const animationEnabled = slides.length > 1;

  // State values for slider logic
  const [paused, setPaused] = useState(false);
  const [trailerOpened, setTrailerOpened] = useState(false);
  const [progress, setProgress] = useState(0.0);

  // Determine if the tab is visible
  const tabVisible = useTabVisible();

  // Get current breakport for mobile background media
  const { xs } = useBreakpoints();

  // Get slider pagination logic
  const [index, goNext, goPrev] = useHeroSliderPagination(slides.length);

  // Create references to background videos and the trailer videos
  const videoRef = useRef([]);
  const trailerRef = useRef(null);

  // Create handlers for swipe recognition
  const swipeHandlers = useSwipeable({
    onSwipedLeft: goNext,
    onSwipedRight: goPrev
  });

  // Reset pause and progress when changing to new slide
  useEffect(() => {
    setProgress(0.0);
    setPaused(false);
  }, [index]);

  // Go to next slide when progress is at 100%
  useEffect(() => {
    if (animationEnabled && progress >= 1.0) {
      goNext();
    }
  }, [progress]);

  // Tick to increase the progress
  useEffect(() => {
    const interval = setInterval(() => {
      if (animationEnabled && !paused && tabVisible) {
        if (slides[index].type === 'image') {
          setProgress(prevProgress => prevProgress + 50 / (slides[index].duration * 1000));
        } else if (slides[index].type === 'video') {
          // Determine progress by video duration. Subtracting 600ms to account for the fade-out animation
          const videoProgress =
            videoRef.current[index]?.currentTime / (videoRef.current[index]?.duration - 0.6) || 0;
          setProgress(Math.min(videoProgress, 1));
        }
      }
    }, 50);
    return () => clearInterval(interval);
  }, [paused, tabVisible, index]);

  // Play video when activating the slide and is in view
  useEffect(() => {
    if (slides[index].type === 'video' && videoRef.current[index] && inView) {
      videoRef.current[index].currentTime = 0;
      videoRef.current[index].play();
      videoRef.current[index].loop = !animationEnabled; // Loop video if it is the only slide
    }
  }, [index]);

  // Pause video when pausing the slide, hiding the browser window or slider is out of view
  useEffect(() => {
    if (slides[index].type === 'video' && videoRef.current[index]) {
      if (paused || !tabVisible || !inView) {
        videoRef.current[index].pause();
      } else if (!paused || tabVisible || inView) {
        videoRef.current[index].play();
      }
    }
  }, [paused, tabVisible, inView]);

  // Calculate slide offset relative to animation direction
  const getOffset = useCallback((slideIndex, currentIndex, slidesCount) => {
    if (slidesCount === 1) return 0; // If there is only one slide, always return offset 0

    // Generate the raw offset (difference of indices)
    let numericOffset = slideIndex - currentIndex;

    // Take care of overlaps at beginning and end
    if (numericOffset <= -1 * slidesCount + 1) {
      numericOffset += slidesCount;
    } else if (numericOffset >= slidesCount - 1) {
      numericOffset -= slidesCount;
    }

    // Add preload to video if it is the next or previous slide
    if (slides[slideIndex].type === 'video') {
      if (
        (slideIndex === currentIndex + 1 || slideIndex === currentIndex - 1) &&
        videoRef?.current[slideIndex]
      ) {
        videoRef.current[slideIndex].preload = 'auto';
      }
    }

    // Clamp number between -1 and +1
    return Math.min(Math.max(numericOffset, -1), 1);
  }, []);

  // Start the trailer playback
  const startTrailer = useCallback(() => {
    setPaused(true);
    setTrailerOpened(true);
  }, []);

  // End the trailer playback
  const closeTrailer = useCallback(() => {
    trailerRef.current?.pause();
    setPaused(false);
    setTrailerOpened(false);
  }, []);

  return (
    <>
      <div ref={appRef} className='s-hero-slider__app'>
        <div className='container s-hero-slider__container' {...swipeHandlers} ref={ref}>
          {/* Slide Media */}
          <div className="s-hero-slider__media-container">
            {slides.map((slide, i) => (
              <div className='s-hero-slider__slide-media' key={i} data-offset={getOffset(i, index, slides.length)}>
                {slide.type === 'image' ? (
                  <figure className='s-hero-slider__background-image'>
                    <picture>
                      <source media="(min-width: 1440px)" srcset={`${slide.backgroundImage?.sizes['2048x2048']}`} />
                      <img
                        src={
                          xs
                            ? slide.backgroundImageMobile?.sizes['large']
                            : slide.backgroundImage?.sizes['2048x2048']
                        }
                        alt={xs ? slide.backgroundImageMobile?.alt : slide.backgroundImage?.alt}
                      />
                    </picture>
                  </figure>
                ) : (
                  <video
                    ref={elem => (videoRef.current[i] = elem)}
                    className='s-hero-slider__background-video'
                    alt={xs ? slide.backgroundVideoMobile?.alt : slide.backgroundVideo?.alt}
                    autobuffer='true'
                    preload='none'
                    controls={false}
                    autoPlay={false}
                    playsInline={true}
                    muted={true}
                    poster={
                      xs
                        ? slide.backgroundVideoMobilePlaceholder?.sizes?.large
                        : slide.backgroundVideoPlaceholder?.sizes?.large
                    }
                    src={xs ? slide.backgroundVideoMobile?.url : slide.backgroundVideo?.url}
                  />
                )}
              </div>
            ))}
          </div>
          {slides.map((slide, i) => (
            <div
              data-offset={getOffset(i, index, slides.length)}
              className='s-hero-slider__slide'
              key={i}
              onFocusCapture={() => setPaused(true)}
            >
              {/* Slide Content */}
              {slide.url !== '' ? (
                <a
                  className='s-hero-slider__slide-content'
                  href={slide.url}
                  dangerouslySetInnerHTML={{ __html: slide.content }}
                  tabIndex={-1}
                />
              ) : (
                <div
                  className='s-hero-slider__slide-content wysiwyg-block'
                  dangerouslySetInnerHTML={{ __html: slide.content }}
                />
              )}

              {/* Slide Trailer */}
              {!slide.trailer ? null : (
                <div className='s-hero-slider__slide-trailer'>
                  <TrailerPreview
                    trailerThumbnail={slide.trailerPlaceholder}
                    onClick={startTrailer}
                    tabIndex={getOffset(i, index, slides.length) == 0 ? 0 : -1}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
        {!animationEnabled ? null : (
          <div className='s-hero-slider__navigation'>
            <HeroSliderNavigation
              currentIndex={index}
              totalCount={slides.length}
              onGoPrev={goPrev}
              onGoNext={goNext}
              progress={progress}
              thumbnails={slides.map(slide => slide.thumbnail)}
              paused={paused}
              onSetPaused={setPaused}
            />
          </div>
        )}
      </div>
      <Lightbox show={trailerOpened} onClose={closeTrailer}>
        <video
          src={trailerOpened && slides[index].trailer ? slides[index].trailer.url : null}
          controls={true}
          loop={false}
          autoPlay={true}
          ref={trailerRef}
        />
      </Lightbox>
    </>
  );
};

HeroSlider.propTypes = {
  definedSlides: PropTypes.arrayOf(
    PropTypes.shape({
      content: PropTypes.string.isRequired,
      type: PropTypes.oneOf(['image', 'video']).isRequired,
      duration: PropTypes.number,
      backgroundImage: PropTypes.object,
      backgroundImageMobile: PropTypes.object,
      backgroundVideo: PropTypes.object,
      backgroundVideoPlaceholder: PropTypes.object,
      backgroundVideoMobile: PropTypes.object,
      backgroundVideoMobilePlaceholder: PropTypes.object,
      thumbnail: PropTypes.object.isRequired,
      trailer: PropTypes.object,
      trailerPlaceholder: PropTypes.object
    })
  ).isRequired
};

export default HeroSlider;
